// @flow
import { useCallback, useEffect, useState, useContext } from 'react';
import { Provider, useDispatch, useSelector } from 'react-redux';
import LumeraPersistGate from '@src/components/LumeraPersistGate';
import _debounce from 'lodash/debounce';
import { stylesheet } from 'typestyle';
import { flexRoot, vertical } from 'csstips';
import classnames from 'classnames';

import DyorDataWrapper from '@components/DyorDataWrapper';
import DesignYourOwnRing from '@components/dyor/Container';
import SearchResults from '@components/bandSearch/SearchResults';

import Filters from '@components/bandSearch/Filters';

import PageSeoContentProvider from '../../PageSeoContentApplier';
import { CanonicalUrlProvider } from '../../../contexts/canonicalUrl';
import {
  SeoContentProvider,
  SeoContentStateContext
} from '../../../contexts/seoContent';

import useMedia from '../../../hooks/useMedia';

import css from '@styles/bandSearch';

import type { ObjectType, RailsContextType } from '@types/misc';

const fetchDebounceRate = 750;

const localCss = stylesheet({
  container: {
    ...flexRoot,
    ...vertical
  }
});

function DyorWithWrapper() {
  return (
    <DyorDataWrapper>
      <Dyor />
    </DyorDataWrapper>
  );
}

function Dyor() {
  const diamondId = useSelector(state => state.ringBuilder.diamond.id);
  const settingSku = useSelector(state => state.ringBuilder.setting.sku);

  if (diamondId || settingSku) {
    return (
      <DesignYourOwnRing key='dyor-chain' style={{ order: 1 }} withWrapper />
    );
  } else {
    return null;
  }
}

function Header() {
  const { h1 } = useContext(SeoContentStateContext);

  if (h1) {
    return (
      <h1 style={{ order: 2 }} className={css.header}>
        {h1}
      </h1>
    );
  } else {
    return null;
  }
}

function SeoDescription() {
  const isMobile = useMedia(['(min-width: 768px)'], [false], true);
  const storeCollapsed = useSelector(
    state => state.productCatalogs.collapseStatus
  );
  const dispatch = useDispatch();
  const [collapsed, setCollapsed] = useState(isMobile || storeCollapsed);
  const { marketing_text } = useContext(SeoContentStateContext);

  if (!marketing_text) return null;

  return (
    <div style={{ order: 3 }} >
      <p className={classnames('clearfix', css.marketingText)}>
        <span
          dangerouslySetInnerHTML={{ __html: marketing_text }}
          className={classnames({ [css.collapsedMarketingText]: collapsed })}
        />
        <ExpandableSeoDescription
          collapsed={collapsed}
          toggle={() => {
            setCollapsed(!collapsed);
            dispatch({
              type: 'UPDATE_MARKETING_TEXT_COLLAPSE_STATUS',
              status: !collapsed
            });
          }}
        />
      </p>
    </div>
  );
}

function ExpandableSeoDescription({ collapsed, toggle }) {
  return (
    <span className={
      classnames({
        [css.collapsedMarketingButton]: collapsed,
        [css.collapseMarketingButton]: true
      })

    }>
      {collapsed ? <span> &hellip; </span> : null}
      &lt;{' '}
      <button onClick={toggle} className='text-link text-underline'>
        show {collapsed ? 'more' : 'less'}
      </button>{' '}
      &gt;
    </span>
  );
}

function BandCatalog() {
  const loading = useSelector(state => state.bandSearch.loading);
  const extents = useSelector(state => state.bandSearchExtents);
  const matches = useSelector(state => state.bands.matches);
  const matchCount = useSelector(state => state.bands.matchCount);
  const filters = useSelector(state => state.bandSearchFilters);
  const [filtersInitializedFromUri, setFiltersInitialized] = useState(false);
  const dispatch = useDispatch();

  const fetchNewResults = useCallback(
    _debounce(() => {
      dispatch({ type: 'FETCH_INITIAL_BANDS' });
    }, fetchDebounceRate),
    []
  );

  useEffect(() => {
    dispatch({
      type: 'SYNC_BAND_FILTERS_FROM_URI',
      callback: () => {
        setFiltersInitialized(true);
      }
    });
  }, [dispatch]);

  useEffect(() => {
    if (!filtersInitializedFromUri) return;

    fetchNewResults();
  }, [filtersInitializedFromUri, filters, fetchNewResults]);

  const handleInfiniteLoad = useCallback(() => {
    if (!filtersInitializedFromUri) return;
    if (loading) return;

    dispatch({ type: 'FETCH_BANDS' });
  }, [filtersInitializedFromUri, loading, dispatch]);

  const allLoaded = matches.length === matchCount;

  return (
    <div className={localCss.container}>
      <DyorWithWrapper />
      <Header />
      <SeoDescription />
      <ResultCount
        matchCount={matchCount}
        allLoaded={!loading && allLoaded}
        allAvailableMatchesCount={extents.allAvailableMatchesCount}
      />
      {filtersInitializedFromUri && (
        <SearchResults
          key='search-results'
          items={matches}
          onInfiniteLoad={handleInfiniteLoad}
          allLoaded={allLoaded}
          matchCount={matchCount}
          isLoading={loading}
          allowGettingMoreResults={
            filtersInitializedFromUri && !loading && !allLoaded
          }
        />
      )}
      <Filters key='filters' priceExtents={extents.price} />
    </div>
  );
}

function ResultCount({ matchCount, allLoaded, allAvailableMatchesCount }) {
  if (matchCount > 0 || allLoaded) {
    const expandMessage = (
      <ClearPriceFilterLink text='expand your price range' />
    );
    let countMessage;
    let displayExpandMessage = false;

    if (matchCount > 0) {
      countMessage = (
        <span>
          <span style={{ fontWeight: 'bold' }}>Results</span> - {matchCount}
        </span>
      );
    } else {
      countMessage = (
        <span>
          <span style={{ fontWeight: 'bold' }}>No results found</span> - Please
          broaden your search
        </span>
      );
    }

    if (
      matchCount > 0 &&
      matchCount < 5 &&
      allAvailableMatchesCount > matchCount
    ) {
      displayExpandMessage = true;
    }

    return (
      <p
        style={{ order: 5 }}
        className={classnames(css.resultsCount, {
          'no-bottom-margin': matchCount === 0
        })}
      >
        {countMessage}
        {displayExpandMessage && <span> (to see more, {expandMessage})</span>}
      </p>
    );
  } else {
    return null;
  }
}

function ClearPriceFilterLink({ text }: { text: string }) {
  const dispatch = useDispatch();

  const clearFilter = () => {
    dispatch({
      type: 'SET_BAND_FILTER',
      payload: {
        filterName: 'price',
        value: undefined
      }
    });
  };

  return (
    <span
      style={{ cursor: 'pointer', textDecoration: 'underline' }}
      onClick={clearFilter}
    >
      {text}
    </span>
  );
}

export default (props: ObjectType, railsContext: RailsContextType) => {
  const store = ReactOnRails.getStore('SharedReduxStore');

  return (
    <Provider store={store}>
      <CanonicalUrlProvider debounceRate={fetchDebounceRate}>
        <SeoContentProvider>
          <PageSeoContentProvider>
            <LumeraPersistGate>
              <BandCatalog {...{ ...props, railsContext }} />
            </LumeraPersistGate>
          </PageSeoContentProvider>
        </SeoContentProvider>
      </CanonicalUrlProvider>
    </Provider>
  );
};
